import clsx from "clsx";
import {
  FC,
  PropsWithChildren,
  ReactNode,
  useId,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import { AVAX, Network, THOR, networkLabel } from "rujira.js";
import {
  AddressValidationError,
  AddressSharePopup,
  Animated,
  Button,
  clipAddress,
  Decimal,
  DenomInput,
  Fiat,
  IconDenom,
  Icons,
  Input,
  NetworkIcon,
  Provider,
  ProviderIcon,
  Radio,
  RujiraLogo,
  SimulationState,
  Warning,
  useTranslation,
} from "rujira.ui";

export type TransferAccountOption = {
  id: string;
  address: string;
  network: Network;
  providers?: Provider.Key[];
  balance?: bigint;
  balanceSymbol?: string;
  valueUsd?: bigint;
};

export const transferAccountKey = (account: {
  address: string;
  asset: { asset: string };
}) => `${account.address}:${account.asset.asset}`;

export const compareBigIntDesc = (left: bigint, right: bigint) =>
  left === right ? 0 : left > right ? -1 : 1;

export const transferFeeValueUsd = (
  amount: bigint,
  decimals: number,
  price?: bigint | null
) =>
  price === undefined || price === null
    ? undefined
    : (amount * price * 10n ** 8n) / (10n ** BigInt(decimals) * 10n ** 12n);

export const transferSimulationFees = (state?: SimulationState) =>
  state?.simulations || (state?.simulation ? [state.simulation] : []);

export const providersForAddress = (
  accounts: readonly { address: { address: string }; provider: Provider.Key }[],
  address: string
) => [
  ...new Set(
    accounts
      .filter((account) => account.address.address === address)
      .map((account) => account.provider)
  ),
];

export const TransferModalNavigation: FC<
  PropsWithChildren<{ onBack: () => void }>
> = ({ onBack, children }) => {
  const { t } = useTranslation("common");
  return (
    <div className="flex ai-c mb-1">
      <button
        type="button"
        className="transparent color-grey hover-white flex ai-c pointer"
        onClick={onBack}>
        <Icons.AngleLeft className="block w-2 h-2 mr-0.5" />
        {t("back")}
      </button>
      <div className="ml-a">{children}</div>
    </div>
  );
};

export const TransferModalHeader: FC<{
  action: ReactNode;
  symbol: string;
  suffix?: ReactNode;
  hideSuffixOnMobile?: boolean;
  description: ReactNode;
}> = ({ action, symbol, suffix, hideSuffixOnMobile, description }) => (
  <header className="transfer-modal__header">
    <h3
      className={clsx("h3 flex ai-c mb-0", {
        "transfer-modal__title--hide-suffix-mobile": hideSuffixOnMobile,
      })}>
      <IconDenom denom={symbol} className="as-c block w-4 h-4 mr-1" />
      <span className="transfer-modal__title-main">
        {action} {symbol}
      </span>
      {suffix && (
        <small className="transfer-modal__title-suffix ml-1 color-grey fw-400">
          {suffix}
        </small>
      )}
    </h3>
    <p className="transfer-modal__description">{description}</p>
  </header>
);

export const TransferForm: FC<
  PropsWithChildren<{
    source?: ReactNode;
    amount: ReactNode;
    destination: ReactNode;
    options?: ReactNode;
    warning?: ReactNode;
    details: ReactNode;
    footer: ReactNode;
  }>
> = ({ source, amount, destination, options, warning, details, footer }) => (
  <div className="transfer-modal__form">
    {source}
    {amount}
    {destination}
    {options}
    {details}
    {warning}
    {footer}
  </div>
);

export const TransferAddressWarning: FC<PropsWithChildren> = ({ children }) => (
  <Warning
    className="warning--sm mt-1.5 mb-1 p-1 condensed flex ai-c"
    color="orange">
    <Animated
      name="exclamation"
      color="orange"
      className="block no-shrink"
      size="2.5rem"
    />
    <div className="text-left">{children}</div>
  </Warning>
);

export const TransferAmountField: FC<
  React.ComponentProps<typeof DenomInput> & {
    label: ReactNode;
    children?: ReactNode;
    errorTooltip?: string;
  }
> = ({ label, children, errorTooltip, invalid, ...props }) => {
  const id = useId();
  return (
    <section
      className="transfer-modal__field"
      data-tooltip-id={invalid && errorTooltip ? "modal-tip" : undefined}
      data-tooltip-content={invalid ? errorTooltip : undefined}>
      <h2 id={id} className="transfer-modal__label">
        {label}
      </h2>
      <DenomInput {...props} invalid={invalid} aria-labelledby={id} />
      {children}
    </section>
  );
};

const AddressActions: FC<{
  address: string;
  onQr: () => void;
  hideQr?: boolean;
}> = ({ address, onQr, hideQr }) => {
  const { t } = useTranslation("common");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success(t("addressCopied"));
    } catch {
      toast.error(t("shareUrlCopyFailed"));
    }
  };
  const share = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({ text: address });
    } catch (error) {
      if ((error as DOMException)?.name !== "AbortError")
        toast.error(t("shareUrlCopyFailed"));
    }
  };

  return (
    <div
      className="transfer-modal__address-actions flex ai-c gap-0.5"
      onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="transparent color-grey hover-white"
        aria-label={t("copyAddress")}
        onClick={() => void copy()}>
        <Icons.Copy className="block w-2 h-2" />
      </button>
      {!hideQr && (
        <button
          type="button"
          className="transparent color-grey hover-white"
          aria-label={t("showQrCode")}
          onClick={onQr}>
          <svg
            aria-hidden="true"
            className="block w-2 h-2"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75">
            <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h3v3h-3zM18 14h3v3M14 18v3h3M19 19h2v2h-2z" />
          </svg>
        </button>
      )}
      <button
        type="button"
        className="transparent color-grey hover-white"
        aria-label={t("share")}
        onClick={() => void share()}>
        <Icons.Share className="block w-2 h-2" />
      </button>
    </div>
  );
};

type CustomAddress = {
  selected: boolean;
  value: string;
  onSelect: () => void;
  onChange: (value: string) => void;
  placeholder: string;
  invalid?: boolean;
  inline?: boolean;
  validationNetwork?: string;
  validationIsRujira?: boolean;
};

export const TransferAccountSelector: FC<{
  label: ReactNode;
  name: string;
  options: TransferAccountOption[];
  value?: string;
  onChange: (option: TransferAccountOption) => void;
  custom?: CustomAddress;
  additional?: {
    id: string;
    label: ReactNode;
    selected: boolean;
    disabled?: boolean;
    onSelect?: () => void;
  };
  onConnect?: () => void;
  showAddressActions?: boolean;
}> = ({
  label,
  name,
  options,
  value,
  onChange,
  custom,
  additional,
  onConnect,
  showAddressActions,
}) => {
  const { t } = useTranslation("common");
  const labelId = useId();
  const [qrOption, setQrOption] = useState<TransferAccountOption>();
  const unique = useMemo(() => {
    const seen = new Set<string>();
    return options.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [options]);
  const selectOption = (option: TransferAccountOption) => {
    onChange(option);
    if (qrOption) setQrOption(option);
  };

  return (
    <section className="transfer-modal__field">
      <h2 id={labelId} className="transfer-modal__label">
        {label}
      </h2>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="transfer-modal__accounts br-1 p-2 bg-black mb-2 flex dir-c gap-y-1">
        {unique.length === 0 && onConnect && (
          <div
            role="radio"
            aria-checked={false}
            tabIndex={0}
            className="transfer-modal__account transfer-modal__empty transparent color-grey flex ai-c fs-14 condensed pointer"
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setQrOption(undefined);
                onConnect();
              }
            }}
            onClick={() => {
              setQrOption(undefined);
              onConnect();
            }}>
            <Radio name={name} checked={false} readOnly />
            <span className="transfer-modal__empty-copy ml-1">{t("noOtherConnectedAddress")}</span>
          </div>
        )}
        {unique.map((option) => {
          const selected = value === option.id;
          return (
            <div
              key={option.id}
              role="radio"
              aria-checked={selected}
              tabIndex={0}
              className={clsx(
                "transfer-modal__account transfer-modal__account--option transparent flex ai-c fs-14 condensed pointer",
                {
                  "color-white": selected,
                  "color-grey": !selected,
                }
              )}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  selectOption(option);
                }
              }}
              onClick={() => selectOption(option)}>
              <Radio name={name} checked={selected} readOnly />
              <span className="transfer-modal__providers flex ai-c ml-1 gap-0.5">
                {option.providers?.map((provider) => (
                  <ProviderIcon
                    key={provider}
                    provider={provider}
                    selected={selected}
                    className="block w-2 h-2"
                  />
                ))}
              </span>
              <span
                className="transfer-modal__account-address inconsolata mx-1 breakall no-shrink"
                data-tooltip-id="modal-tip"
                data-tooltip-content={option.address}>
                {clipAddress(option.address)}
              </span>
              <span className="flex ai-c gap-0.5 no-shrink">
                {option.network === THOR ? (
                  <RujiraLogo showText={false} className="block w-2 h-2" />
                ) : (
                  <NetworkIcon
                    network={option.network}
                    className="block w-2 h-2"
                  />
                )}
                <span className="transfer-modal__network-label--desktop">
                  {networkLabel(option.network)}
                </span>
                <span className="transfer-modal__network-label--mobile">
                  {option.network === AVAX
                    ? "Avalanche"
                    : networkLabel(option.network)}
                </span>
              </span>
              {option.balance !== undefined && (
                <span className="transfer-modal__account-balance ml-a">
                  <span
                    className={clsx("tag tag--md", { "tag--teal": selected })}>
                    <Decimal
                      amount={option.balance}
                      symbol={option.balanceSymbol}
                      clip
                      symbolClassName="color-grey condensed fw-500"
                    />
                  </span>
                  <span className="color-white ml-1">
                    (
                    <Fiat
                      amount={option.valueUsd || 0n}
                      symbol="$"
                      decimals={8}
                      clip
                    />
                    )
                  </span>
                </span>
              )}
              {showAddressActions && (
                <span className="transfer-modal__account-actions ml-a">
                  <AddressActions
                    address={option.address}
                    onQr={() =>
                      setQrOption((current) =>
                        current?.id === option.id ? undefined : option
                      )
                    }
                  />
                </span>
              )}
            </div>
          );
        })}
        {additional && (
          <div
            role="radio"
            aria-checked={additional.selected}
            aria-disabled={additional.disabled}
            tabIndex={additional.disabled ? -1 : 0}
            className={clsx(
              "transfer-modal__account transparent flex ai-c fs-14 condensed",
              {
                "color-white": additional.selected,
                "color-grey": !additional.selected,
              }
            )}
            onKeyDown={(event) => {
              if (
                !additional.disabled &&
                (event.key === "Enter" || event.key === " ")
              ) {
                event.preventDefault();
                setQrOption(undefined);
                additional.onSelect?.();
              }
            }}
            onClick={() => {
              if (additional.disabled) return;
              setQrOption(undefined);
              additional.onSelect?.();
            }}>
            <Radio name={name} checked={additional.selected} readOnly />
            <span className="ml-1">{additional.label}</span>
          </div>
        )}
        {custom && (
          <div
            role="radio"
            aria-checked={custom.selected}
            tabIndex={0}
            className={clsx(
              "transfer-modal__account transparent flex ai-c fs-14 condensed pointer",
              {
                "color-white": custom.selected,
                "color-grey": !custom.selected,
              }
            )}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setQrOption(undefined);
                custom.onSelect();
              }
            }}
            onClick={() => {
              setQrOption(undefined);
              custom.onSelect();
            }}>
            <Radio name={name} checked={custom.selected} readOnly />
            <span className="ml-1 no-shrink">{t("otherCustomAddress")}</span>
            {custom.selected && custom.inline !== false && (
              <Input
                autoFocus
                value={custom.value}
                onChange={(event) => custom.onChange(event.currentTarget.value)}
                onClick={(event) => event.stopPropagation()}
                placeholder={custom.placeholder}
                containerClassName={clsx("transfer-modal__custom-input ml-a", {
                  "input-container--error": custom.invalid,
                })}
                className="input--inline input--placeholder-visible text-right"
              />
            )}
          </div>
        )}
      </div>
      {custom?.invalid && custom.validationNetwork && (
        <AddressValidationError
          network={custom.validationNetwork}
          isRujira={custom.validationIsRujira}
        />
      )}
      {qrOption && (
        <div className="transfer-modal__qr-inline">
          <AddressSharePopup
            address={qrOption.address}
            network={qrOption.network}
          />
        </div>
      )}
    </section>
  );
};

export const TransactionDetails: FC<
  PropsWithChildren<{ title: ReactNode; titleClassName?: string }>
> = ({ title, titleClassName, children }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <section className="transfer-modal__details">
      <h2
        className={clsx(
          "fs-14 fw-500 color-grey mb-1 ml-1",
          titleClassName || "mt-1"
        )}>
        <button
          type="button"
          className="transparent flex ai-c pointer color-grey hover-white"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}>
          {title}
          {expanded ? (
            <Icons.AngleUp className="block w-2 h-2 ml-0.5" />
          ) : (
            <Icons.AngleDown className="block w-2 h-2 ml-0.5" />
          )}
        </button>
      </h2>
      {expanded && (
        <div className="row wrap condensed fs-14 px-2 gap-y-1">{children}</div>
      )}
    </section>
  );
};

export const TransactionDetailRow: FC<
  PropsWithChildren<{ label: ReactNode; tooltip?: string }>
> = ({ label, tooltip, children }) => (
  <>
    <div className="transfer-modal__detail-label col-5 col-xs-4 text-left color-grey fw-500">
      <span className="transfer-modal__detail-bullet" aria-hidden="true">
        &bull;&nbsp;
      </span>
      {tooltip ? (
        <span
          className="has-tip no-select"
          data-tooltip-id="modal-tip"
          data-tooltip-html={`<div class="w-30 text-left">${tooltip}</div>`}
          style={{ borderBottom: "1px dotted currentColor" }}>
          {label}
        </span>
      ) : (
        label
      )}
    </div>
    <div className="transfer-modal__detail-value col-7 col-xs-8 text-right">
      {children}
    </div>
  </>
);

export const TransactionAddress: FC<{
  address?: string;
  network?: Network;
}> = ({ address, network }) =>
  address ? (
    <span className="flex ai-c jc-e nowrap">
      {network && (
        <NetworkIcon network={network} className="block w-2 h-2 mr-1" />
      )}
      <span
        className="inconsolata"
        data-tooltip-id="modal-tip"
        data-tooltip-content={address}>
        {clipAddress(address)}
      </span>
    </span>
  ) : (
    <span className="color-grey">&mdash;</span>
  );

export const TransferFooter: FC<{
  cancel: () => void;
  action: ReactNode;
}> = ({ cancel, action }) => {
  const { t } = useTranslation("common");
  return (
    <footer className="transfer-modal__footer">
      <Button
        className="button--grey button--outline"
        onClick={cancel}
        label={t("cancel")}
      />
      {action}
    </footer>
  );
};
